<template>
    <v-dialog v-model="dialog" width="400" persistent>
        <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on" class="d-inline">
                <slot></slot>
            </div>
        </template>
        <v-card>
            <v-card-title class="flex-nowrap align-start">
                {{ title }}
                <v-spacer></v-spacer>
                <v-btn @click="dialog = false" icon><v-icon>clear</v-icon></v-btn>
            </v-card-title>
            <v-card-text class="mt-4">
                <v-form v-model="valid" ref="form">
                    <v-text-field
                        v-model="quotaValue"
                        v-mask="currencyMask"
                        :rules="[rules.required, rules.positiveFloat]"
                        outlined
                        dense
                        prefix="AC"></v-text-field>
                    <v-menu v-model="endDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                                v-model="endDate"
                                label="End date"
                                :rules="[rules.required, rules.endDateWithinBounds]"
                                :hint="endDateHint"
                                :persistent-hint="Boolean(endDateHint)"
                                readonly
                                outlined
                                dense
                                v-bind="attrs"
                                v-on="on"></v-text-field>
                        </template>
                        <nv-date-picker v-model="endDate" :min="minEndDate" :max="maxEndDate" @input="endDateMenu = false"></nv-date-picker>
                    </v-menu>
                    <v-sheet rounded class="d-flex align-center py-2 px-4">
                        <v-switch v-model="resetCounter" inset dense hide-details label="Reset counter" class="my-0 pa-0"></v-switch>
                        <v-btn
                            icon
                            small
                            href="https://docs.nuvolos.com/how-to-guides/workflows-for-instructors/courses-with-gpus#fixed-quota-schedule"
                            target="_blank"
                            class="ml-3">
                            <v-icon small>mdi-help-circle-outline</v-icon>
                        </v-btn>
                    </v-sheet>
                </v-form>
                <v-alert type="error" text v-if="error">{{ error }}</v-alert>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>

                <v-btn color="primary" text v-if="!success" :loading="sending" :disabled="!valid" @click="saveQuota()">{{ ctaLabel }}</v-btn>
                <v-btn color="success" v-if="success"><v-icon>mdi-check</v-icon></v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import NvDatePicker from '@/components/NvDatePicker.vue'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'
import { DateTime } from 'luxon'
import { toLocaleFixed } from '@/utils'
import { createBackendErrorHandler } from '@/utils/errorMessages'
import * as instanceErrors from '@/modules/instance/errors'
import * as lifecycleErrors from '@/modules/snapshot/errors/lifecycle'
import * as permissionsErrors from '@/utils/errors/permissions'
import * as resourcePoolErrors from '@/modules/user/errors/resourcePools'
import * as spaceErrors from '@/modules/space/errors'

const { getBackendErrorMessage } = createBackendErrorHandler(instanceErrors, lifecycleErrors, permissionsErrors, resourcePoolErrors, spaceErrors)

const currencyMask = createNumberMask({
    prefix: '',
    allowDecimal: true
})

const defaultQuotaValue = '0.00'

export default {
    name: 'InstanceCreditQuotaEditDialog',
    components: { NvDatePicker },
    mixins: [],
    props: {
        crud: { type: String, default: 'auto' },
        quota: { type: Object, default: null },
        minCreateEndDate: { type: String, default: '' }
    },
    data() {
        return {
            dialog: false,
            currencyMask,
            rules: {
                required: value => !!value || 'This field is required',
                positiveFloat: value => {
                    const num = parseFloat(value.replace(/,/g, ''))
                    return (!isNaN(num) && num > 0) || 'Value must be a positive number greater than 0'
                },
                endDateWithinBounds: value => {
                    const selectedDate = DateTime.fromISO(value || '').startOf('day')
                    if (!selectedDate?.isValid) {
                        return 'This field is required'
                    }

                    const minDate = DateTime.fromISO(this.minEndDate || '').startOf('day')
                    if (minDate?.isValid && selectedDate < minDate) {
                        return `End date cannot be earlier than ${minDate.toISODate()}`
                    }

                    const maxDate = DateTime.fromISO(this.maxEndDate || '').startOf('day')
                    if (maxDate?.isValid && selectedDate > maxDate) {
                        return `End date cannot be later than ${maxDate.toISODate()}`
                    }

                    return true
                }
            },
            quotaValue: defaultQuotaValue,
            endDate: '',
            resetCounter: true,
            endDateMenu: false,
            sending: false,
            success: false,
            error: null,
            valid: false
        }
    },
    methods: {
        getTodayDate() {
            return DateTime.now().toISODate() || ''
        },
        normalizeEndDate(value) {
            const today = DateTime.now().startOf('day')
            const parsedDate = DateTime.fromISO(value || '')
            if (parsedDate.isValid && parsedDate.startOf('day') >= today) {
                return parsedDate.toISODate() || this.getTodayDate()
            }
            return this.getTodayDate()
        },
        formatAC(value, precision = 2) {
            return toLocaleFixed(value, precision)
        },
        async saveQuota() {
            this.error = null
            this.success = false
            this.sending = true

            try {
                const payload = {
                    limit: this.quotaInNumber,
                    end_date: this.endDate,
                    carry_over: !this.resetCounter
                }

                if (this.resolvedCrud === 'create') {
                    await this.$axios.post(`/space_credit_limit/${this.$route.params.sid}/new`, payload)
                } else {
                    if (!this.quota?.sclid) {
                        throw new Error('Missing schedule id for quota update.')
                    }
                    await this.$axios.patch(`/space_credit_limit/${this.quota.sclid}`, payload)
                }

                this.$store.dispatch('spaceStore/refreshStudentQuotaData', this.$route.params.sid)

                this.success = true
                setTimeout(() => {
                    this.dialog = false
                    this.$emit('success')
                }, 2000)
            } catch (error) {
                this.error = getBackendErrorMessage(error, error?.response?.data?.message || error.message || 'An error occurred while saving the file quota.')
            } finally {
                this.sending = false
            }
        }
    },
    computed: {
        hasPrefill() {
            return this.quota?.sclid != null || this.quota?.end_date != null || this.quota?.limit != null || this.quota?.carry_over != null
        },
        resolvedCrud() {
            if (this.crud === 'create' || this.crud === 'update') {
                return this.crud
            }
            return this.hasPrefill ? 'update' : 'create'
        },
        title() {
            return this.resolvedCrud === 'create' ? 'New Instance Credit Quota' : 'Edit Instance Credit Quota'
        },
        ctaLabel() {
            return this.resolvedCrud === 'create' ? 'Create' : 'Save'
        },
        editMinEndDate() {
            if (this.resolvedCrud !== 'update') {
                return null
            }

            const previousDate = DateTime.fromISO(this.quota?.previous_date || '').startOf('day')
            if (previousDate?.isValid) {
                return previousDate.plus({ days: 1 }).toISODate()
            }

            return DateTime.now().plus({ days: 1 }).toISODate() || this.getTodayDate()
        },
        editMaxEndDate() {
            if (this.resolvedCrud !== 'update') {
                return null
            }

            const nextDate = DateTime.fromISO(this.quota?.next_date || '').startOf('day')
            if (nextDate?.isValid) {
                return nextDate.minus({ days: 1 }).toISODate()
            }

            return null
        },
        minEndDate() {
            if (this.resolvedCrud === 'create') {
                const parsed = DateTime.fromISO(this.minCreateEndDate || '')
                if (parsed.isValid) {
                    return parsed.plus({ days: 1 }).toISODate() || this.getTodayDate()
                }

                return DateTime.now().plus({ days: 1 }).toISODate() || this.getTodayDate()
            }

            const today = DateTime.fromISO(this.getTodayDate()).startOf('day')
            const editMin = DateTime.fromISO(this.editMinEndDate || '').startOf('day')

            if (today?.isValid && editMin?.isValid) {
                return (today > editMin ? today : editMin).toISODate() || this.getTodayDate()
            }

            if (editMin?.isValid) {
                return editMin.toISODate() || this.getTodayDate()
            }

            return this.getTodayDate()
        },
        maxEndDate() {
            if (this.resolvedCrud === 'update') {
                return this.editMaxEndDate
            }
            return null
        },
        endDateHint() {
            if (this.resolvedCrud !== 'update') {
                return ''
            }

            if (this.maxEndDate) {
                return `Valid date range: ${this.minEndDate} - ${this.maxEndDate}`
            }

            return `Valid date range: ${this.minEndDate} - any future date`
        },
        quotaInNumber() {
            return parseFloat(this.quotaValue.replace(/,/g, ''))
        }
    },
    watch: {
        dialog: {
            handler(open) {
                if (open) {
                    if (this.resolvedCrud === 'create') {
                        this.quotaValue = defaultQuotaValue
                        this.endDate = this.minEndDate
                        this.resetCounter = true
                    } else {
                        this.quotaValue = this.formatAC(this.quota?.limit, 2) || defaultQuotaValue
                        const currentDate = DateTime.fromISO(this.quota?.end_date || '').startOf('day')
                        const minDate = DateTime.fromISO(this.minEndDate || '').startOf('day')
                        const maxDate = DateTime.fromISO(this.maxEndDate || '').startOf('day')

                        if (currentDate?.isValid) {
                            if (minDate?.isValid && currentDate < minDate) {
                                this.endDate = minDate.toISODate() || this.minEndDate
                            } else if (maxDate?.isValid && currentDate > maxDate) {
                                this.endDate = maxDate.toISODate() || this.minEndDate
                            } else {
                                this.endDate = currentDate.toISODate() || this.minEndDate
                            }
                        } else {
                            this.endDate = this.minEndDate
                        }
                        this.resetCounter = !this.quota?.carry_over
                    }
                    this.success = false
                    this.error = null
                    this.$nextTick(() => {
                        this.$refs.form?.resetValidation()
                    })
                }
            },
            immediate: true
        }
    }
}
</script>
